import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useTrackVisibility } from "@/hooks/use-track-visibility";
import { useColumnResize } from "@/hooks/use-column-resize";
import posthog from "@/lib/posthog";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/enhanced-button";
import { ResizeHandle } from "@/components/ui/resize-handle";
import { TruncatedCell } from "@/components/ui/TruncatedCell";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { X, ArrowUpDown, ArrowUp, ArrowDown, ChevronDown, Filter, ChevronLeft, ChevronRight, Bell, Bot, CreditCard, Check, Download, Settings2, MessageSquare } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { exportExpensesCsv } from "@/services/export.service";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { format } from "date-fns";
import { DateFilterKey, resolveDateFilter, DATE_FILTER_OPTIONS } from "@/utils/dateFilters";
import { DateFilter } from "@/components/ui/DateFilter";
import { useIsMobile } from "@/hooks/use-mobile";
import { useToast } from "@/hooks/use-toast";
import {
  formatDate,
  formatAmount,
  formatAmountCompact,
  formatStatusDisplay,
  getStatusBadgeVariant,
  getSourceTypeBadgeColor
} from "@/utils/expenseFormatters";
import { ExpensePayableItem, VendorPayment, ExpensePayablePagination, ExpensePayableApiResponse, PayablesKPIs } from "@blinkbooks/types";
import apiClient from "@/utils/httpClient";
import { fetchPayablesKPIs, fetchAgedPayablesKPIs } from "@/services/kpi.service";
import { AgedPayablesKPIs } from "@blinkbooks/types";
import { ReminderCreateModal } from "@/components/ReminderCreateModal";
import CommentsSheet, { CommentsSheetTarget } from "@/components/CommentsSheet";
import { UserOrganizationRole } from "@blinkbooks/types";
import { KpiGroupCard } from "@/components/ui/KpiGroupCard";
import { useOrganization } from "@/contexts/OrganizationContext";
import { listBillWorkflowStatuses, setBillStatus } from "@/services/billWorkflowStatus.service";
import { ManageBillStatusesModal } from "@/components/ManageBillStatusesModal";
import type { BillWorkflowStatusDto } from "@blinkbooks/types";

interface RowReminder {
  text: string;
  targetNames: string[];
}

// ── Ramp placeholder icon ────────────────────────────────────────────────────
const RampIcon = () => (
  <svg viewBox="0 0 20 20" className="h-3.5 w-3.5" aria-hidden="true">
    <rect width="20" height="20" rx="4" fill="#00D54B" />
    <text x="10" y="15" textAnchor="middle" fill="white" fontSize="13" fontWeight="700" fontFamily="sans-serif">R</text>
  </svg>
);

// ── Badges ───────────────────────────────────────────────────────────────────
const StatusBadge = ({ status }: { status: string }) => (
  <Badge variant={getStatusBadgeVariant(status)}>{formatStatusDisplay(status)}</Badge>
);
const SourceTypeBadge = ({ sourceType }: { sourceType: string }) => (
  <Badge className={getSourceTypeBadgeColor(sourceType)}>{sourceType}</Badge>
);

const WorkflowStatusBadge = ({
  item,
  statuses,
  onChange,
}: {
  item: ExpensePayableItem;
  statuses: BillWorkflowStatusDto[];
  onChange: (billId: string, statusId: string | null) => void;
}) => {
  if (item.source_type !== 'bill') return <span className="text-xs text-muted-foreground">—</span>;
  const current = statuses.find(s => s.id === item.workflow_status_id);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          onClick={(e) => e.stopPropagation()}
          className="text-xs px-2 py-1 rounded-full border font-medium truncate max-w-full"
          style={current
            ? { backgroundColor: `${current.color}1A`, color: current.color, borderColor: `${current.color}40` }
            : { color: 'var(--muted-foreground)' }}
        >
          {current ? current.label : 'Set status'}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-48" onClick={(e) => e.stopPropagation()}>
        {statuses.map(s => (
          <DropdownMenuItem key={s.id} onClick={() => onChange(item.id, s.id)} className="gap-2 cursor-pointer">
            <span className="h-2 w-2 rounded-full flex-shrink-0" style={{ backgroundColor: s.color }} />
            <span className="truncate">{s.label}</span>
          </DropdownMenuItem>
        ))}
        {statuses.length === 0 && (
          <div className="px-2 py-1.5 text-xs text-muted-foreground">No statuses configured</div>
        )}
        {item.workflow_status_id && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => onChange(item.id, null)} className="cursor-pointer text-muted-foreground">
              Clear status
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

// ── Payment detail sheet ─────────────────────────────────────────────────────
const Detail = ({ label, value }: { label: string; value: string }) => (
  <div className="flex justify-between text-sm">
    <span className="text-muted-foreground">{label}</span>
    <span className="font-medium text-right max-w-[60%] break-words">{value}</span>
  </div>
);

const VendorPaymentDetailSheet = ({ payment, open, onClose }: {
  payment: VendorPayment | null; open: boolean; onClose: () => void;
}) => (
  <Sheet open={open} onOpenChange={v => !v && onClose()}>
    <SheetContent side="right" className="w-full max-w-md overflow-y-auto">
      <SheetHeader>
        <SheetTitle className="flex items-center gap-2"><CreditCard className="h-5 w-5" />Payment Details</SheetTitle>
      </SheetHeader>
      {payment && (
        <div className="mt-6 rounded-lg border bg-muted/20 p-4 space-y-3">
          <Detail label="Payment Date" value={formatDate(payment.datePaid)} />
          <Detail label="Amount" value={formatAmount(payment.amount, payment.currency)} />
          <Detail label="Currency" value={payment.currency} />
          <Detail label="Method" value={payment.paymentMethod || "—"} />
          <Detail label="Reference #" value={payment.referenceNumber || "—"} />
          {payment.billId && <Detail label="Bill ID" value={payment.billId} />}
          {payment.expenseId && <Detail label="Expense ID" value={payment.expenseId} />}
          <Detail label="Created" value={formatDate(payment.createdAt)} />
        </div>
      )}
    </SheetContent>
  </Sheet>
);

// ── Payment sub-rows ─────────────────────────────────────────────────────────
const PaymentDetails = ({ payments, onPaymentClick }: {
  payments: VendorPayment[]; onPaymentClick: (p: VendorPayment) => void;
}) => {
  if (payments.length === 0) return <div className="p-4 text-sm text-muted-foreground">No payments found for this item.</div>;
  return (
    <div className="p-3 md:p-4 bg-muted/5 border-t">
      <h4 className="text-sm font-semibold mb-3">Related Payments</h4>
      <div className="space-y-2">
        {payments.map(p => (
          <div key={p.id} onClick={() => onPaymentClick(p)}
            className="grid grid-cols-1 sm:grid-cols-4 gap-2 md:gap-3 text-sm p-3 bg-background rounded border cursor-pointer hover:bg-muted/20 transition-colors">
            <div><span className="text-muted-foreground text-xs block">Payment Date</span><span className="font-medium">{formatDate(p.datePaid)}</span></div>
            <div><span className="text-muted-foreground text-xs block">Amount</span><span className="font-medium">{formatAmount(p.amount, p.currency)}</span></div>
            <div><span className="text-muted-foreground text-xs block">Method</span><span className="font-medium">{p.paymentMethod || '—'}</span></div>
            <div><span className="text-muted-foreground text-xs block">Ref #</span><span className="font-medium">{p.referenceNumber || '—'}</span></div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ── Actions cell ─────────────────────────────────────────────────────────────
interface ActionsProps {
  item: ExpensePayableItem;
  onAskChat: (msg: string) => void;
  onReminder: () => void;
  onComment: () => void;
  rowReminder: RowReminder | undefined;
  commentCount: number | undefined;
}
const ActionsCell = ({ item, onAskChat, onReminder, onComment, rowReminder, commentCount }: ActionsProps) => {
  const { activeViewId } = useOrganization();
  const buildContext = () => [
    `[${item.source_type.charAt(0).toUpperCase() + item.source_type.slice(1)} Context]`,
    `Vendor: ${item.vendor_name}`,
    `Category: ${item.category_name}`,
    `Amount: ${formatAmount(item.amount, item.currency)}`,
    `Status: ${formatStatusDisplay(item.status)}`,
    `Txn Date: ${formatDate(item.txn_date)}`,
    ...(item.due_date ? [`Due Date: ${formatDate(item.due_date)}`] : []),
    ...(item.payment_date ? [`Payment Date: ${formatDate(item.payment_date)}`] : []),
    ...(item.payment_method ? [`Payment Method: ${item.payment_method}`] : []),
  ].join('\n');

  const showPayButton = item.source_type === 'bill' && (item.status === 'unpaid' || item.status === 'partially_paid');

  return (
    <div className="flex items-center gap-1 h-full">
      {showPayButton && (
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="h-7 w-7 flex items-center justify-center rounded opacity-40 cursor-not-allowed">
              <RampIcon />
            </span>
          </TooltipTrigger>
          <TooltipContent side="left" className="text-xs">Pay via Ramp (coming soon)</TooltipContent>
        </Tooltip>
      )}
      {!activeViewId && (
        <Button variant="ghost" size="sm" onClick={onReminder}
          className="h-7 w-7 p-0 hover:bg-orange-100 transition-opacity" title="Set reminder">
          <Bell className="h-3.5 w-3.5 text-orange-600" />
        </Button>
      )}
      {!activeViewId && item.source_type === 'bill' && (
        <Button variant="ghost" size="sm" onClick={onComment}
          className="h-7 w-7 p-0 hover:bg-blue-100 transition-opacity relative" title="Comments">
          <MessageSquare className="h-3.5 w-3.5 text-blue-600" />
          {!!commentCount && (
            <span className="absolute -top-0.5 -right-0.5 h-3.5 w-3.5 rounded-full bg-blue-500 text-[9px] font-bold text-white flex items-center justify-center leading-none">
              {commentCount > 9 ? '9+' : commentCount}
            </span>
          )}
        </Button>
      )}
      <Button variant="ghost" size="sm" onClick={() => onAskChat(buildContext())}
        className="h-7 w-7 p-0 hover:bg-violet-100 transition-opacity" title="Ask AI about this item">
        <Bot className="h-3.5 w-3.5 text-violet-600" />
      </Button>
      {rowReminder && (
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="h-2 w-2 rounded-full bg-orange-400 flex-shrink-0 ml-1 cursor-default" />
          </TooltipTrigger>
          <TooltipContent side="left" className="max-w-xs text-xs space-y-1">
            <p className="font-semibold">Reminder set</p>
            <p>{rowReminder.text}</p>
            <p className="text-muted-foreground">→ {rowReminder.targetNames.join(', ')}</p>
          </TooltipContent>
        </Tooltip>
      )}
    </div>
  );
};

// ── Mobile card ──────────────────────────────────────────────────────────────
const MobileExpenseCard = ({ item, isExpanded, onToggle, vendorPaymentsCache, isLoading, onPaymentClick }: {
  item: ExpensePayableItem; isExpanded: boolean; onToggle: () => void;
  vendorPaymentsCache: Map<string, VendorPayment[]>; isLoading: boolean; onPaymentClick: (p: VendorPayment) => void;
}) => {
  const payments = vendorPaymentsCache.get(item.id) || [];
  return (
    <Card className="mb-3">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <SourceTypeBadge sourceType={item.source_type} />
              <StatusBadge status={item.status} />
            </div>
            <h3 className="font-semibold text-sm truncate">{item.vendor_name}</h3>
            <p className="text-xs text-muted-foreground truncate">{item.category_name}</p>
          </div>
          <div className="text-right flex-shrink-0">
            <div className="font-bold text-base">{formatAmount(item.amount, item.currency)}</div>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2 text-sm pt-2 border-t">
          <div><span className="text-muted-foreground text-xs">Txn Date</span><div className="font-medium">{formatDate(item.txn_date)}</div></div>
          <div><span className="text-muted-foreground text-xs">Due Date</span><div className="font-medium">{formatDate(item.due_date)}</div></div>
        </div>
        <div className="flex justify-end pt-2 border-t">
          <Button variant="ghost" size="sm" onClick={onToggle} className="h-8 px-3">
            {isExpanded ? 'Hide' : 'Show'} Payments
            <ChevronDown className={`h-4 w-4 ml-1 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
          </Button>
        </div>
        {isExpanded && (
          <div className="pt-2 border-t">
            {isLoading ? <div className="p-4 text-sm text-muted-foreground text-center">Loading payments…</div>
              : <PaymentDetails payments={payments} onPaymentClick={onPaymentClick} />}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ── Types ────────────────────────────────────────────────────────────────────
type SortField = 'reference_number' | 'source_type' | 'vendor_name' | 'category_name' | 'txn_date' | 'due_date' | 'amount' | 'balance' | 'status';
type SortDirection = 'asc' | 'desc' | null;

const vendorPaymentsCache = new Map<string, VendorPayment[]>();

interface ExpensesPayablesProps {
  vendorFilter?: string | null;
  onAskChat: (message: string) => void;
  dateFilterKey: DateFilterKey | '';
  dateCustomFrom?: Date;
  dateCustomTo?: Date;
  onDateFilterChange: (key: DateFilterKey | '', from?: Date, to?: Date) => void;
  currentUserId?: string;
  role?: UserOrganizationRole | null;
}

const EXPENSES_COL_WIDTHS = [80, 80, 130, 120, 100, 110, 100, 95, 100, 95, 95, 90];
const EXPENSES_COL_WIDTHS_WITH_COMPANY = [80, 130, 80, 130, 120, 100, 110, 100, 95, 100, 95, 95, 90];

const ALL_STATUSES = ['paid', 'unpaid', 'partially_paid', 'void'];

// ── Main component ───────────────────────────────────────────────────────────
const ExpensesPayables = ({ vendorFilter, onAskChat, dateFilterKey, dateCustomFrom, dateCustomTo, onDateFilterChange, currentUserId, role }: ExpensesPayablesProps) => {
  const visibilityRef = useTrackVisibility("expenses_payables_table");
  const { activeViewId } = useOrganization();
  const colWidths = activeViewId ? EXPENSES_COL_WIDTHS_WITH_COMPANY : EXPENSES_COL_WIDTHS;
  const { gridTemplate, totalWidth, startResize } = useColumnResize(colWidths);
  const companyColOffset = activeViewId ? 1 : 0;
  const [workflowStatuses, setWorkflowStatuses] = useState<BillWorkflowStatusDto[]>([]);
  const [showManageStatuses, setShowManageStatuses] = useState(false);
  const [expensePayables, setExpensePayableItems] = useState<ExpensePayableItem[]>([]);
  const [expandedItemId, setExpandedItemId] = useState<string | null>(null);
  const [selectedPayment, setSelectedPayment] = useState<VendorPayment | null>(null);
  const [paymentSheetOpen, setPaymentSheetOpen] = useState(false);
  const [sortField, setSortField] = useState<SortField>('due_date');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [isLoadingData, setIsLoadingData] = useState(false);
  const isMobile = useIsMobile();

  const [reminderModal, setReminderModal] = useState<{ itemId: string; label: string; type: 'bill' | 'expense' } | null>(null);
  const [rowReminders, setRowReminders] = useState<Map<string, RowReminder>>(new Map());
  const [commentsTarget, setCommentsTarget] = useState<CommentsSheetTarget | null>(null);
  const [commentCounts, setCommentCounts] = useState<Map<string, number>>(new Map());

  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState<ExpensePayablePagination>({ page: 1, limit: 25, total: 0, totalPages: 0 });
  const [cacheVersion, setCacheVersion] = useState(0);
  const [loadingPayments, setLoadingPayments] = useState<Set<string>>(new Set());
  const [isExporting, setIsExporting] = useState(false);
  const [payablesKpis, setPayablesKpis] = useState<PayablesKPIs | null>(null);
  const [agedPayablesKpis, setAgedPayablesKpis] = useState<AgedPayablesKPIs | null>(null);
  const [kpisLoading, setKpisLoading] = useState(false);
  const [highlightedItemKey, setHighlightedItemKey] = useState<string | null>(null);
  const rowRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const { toast } = useToast();

  // ── Filter state ────────────────────────────────────────────────────────
  const [selectedVendors, setSelectedVendors] = useState<string[]>([]);
  const [selectedSourceTypes, setSelectedSourceTypes] = useState<string[]>([]);
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [amountMin, setAmountMin] = useState<string>('');
  const [amountMax, setAmountMax] = useState<string>('');

  const [showOverdue, setShowOverdue] = useState(false);

  const [appliedFilters, setAppliedFilters] = useState(() => ({
    vendors: [] as string[], sourceTypes: [] as string[], statuses: [] as string[], categories: [] as string[], companies: [] as string[],
    dateFilterKey,
    dateCustomFrom,
    dateCustomTo,
    amountMin: '', amountMax: '', showOverdue: false,
  }));

  // Keep applied date filters in sync with global date prop changes
  const appliedFiltersRef = useRef(appliedFilters);
  useEffect(() => { appliedFiltersRef.current = appliedFilters; });
  const prevDateRef = useRef({ dateFilterKey, dateCustomFrom, dateCustomTo });
  useEffect(() => {
    const prev = prevDateRef.current;
    if (prev.dateFilterKey === dateFilterKey &&
        prev.dateCustomFrom?.getTime() === dateCustomFrom?.getTime() &&
        prev.dateCustomTo?.getTime() === dateCustomTo?.getTime()) return;
    prevDateRef.current = { dateFilterKey, dateCustomFrom, dateCustomTo };
    const newFilters = { ...appliedFiltersRef.current, dateFilterKey, dateCustomFrom, dateCustomTo };
    setAppliedFilters(newFilters);
    setCurrentPage(1);
    fetchData(1, newFilters);
    fetchKPIs(newFilters);
  // fetchData/fetchKPIs intentionally excluded — called with explicit args, no stale closure risk
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateFilterKey, dateCustomFrom, dateCustomTo]);

  const [allFetchedVendors, setAllFetchedVendors] = useState<Set<string>>(new Set());
  const [allFetchedCategories, setAllFetchedCategories] = useState<Set<string>>(new Set());
  const [allFetchedOrganizations, setAllFetchedOrganizations] = useState<Map<string, string>>(new Map());
  const [selectedCompanies, setSelectedCompanies] = useState<string[]>([]);

  useEffect(() => {
    listBillWorkflowStatuses().then(setWorkflowStatuses).catch(() => setWorkflowStatuses([]));
  }, []);

  const handleWorkflowStatusChange = useCallback(async (billId: string, statusId: string | null) => {
    const status = statusId ? workflowStatuses.find(s => s.id === statusId) ?? null : null;
    let previous: { workflow_status_id: string | null; workflow_status_label: string | null; workflow_status_color: string | null } | null = null;
    setExpensePayableItems(prev => prev.map(it => {
      if (it.id !== billId) return it;
      previous = { workflow_status_id: it.workflow_status_id, workflow_status_label: it.workflow_status_label, workflow_status_color: it.workflow_status_color };
      return { ...it, workflow_status_id: statusId, workflow_status_label: status?.label ?? null, workflow_status_color: status?.color ?? null };
    }));
    try {
      await setBillStatus(billId, statusId);
    } catch {
      setExpensePayableItems(prev => prev.map(it => (it.id === billId && previous) ? { ...it, ...previous } : it));
      toast({ variant: 'destructive', description: 'Failed to update status. Please try again.' });
    }
  }, [workflowStatuses, toast]);


  const fetchData = useCallback(async (
    page: number = 1,
    filters = appliedFilters,
    sortOverride?: { sortBy: 'amount'; sortOrder: 'asc' | 'desc' },
  ) => {
    setIsLoadingData(true);
    try {
      const params = new URLSearchParams();
      params.set('page', page.toString());
      params.set('limit', '25');
      if (filters.vendors.length > 0) params.set('vendor', filters.vendors.join(','));
      if (filters.sourceTypes.length > 0) params.set('sourceType', filters.sourceTypes.join(','));
      if (filters.statuses.length > 0) params.set('status', filters.statuses.join(','));
      if (filters.categories.length > 0) params.set('category', filters.categories.join(','));
      if (filters.companies.length > 0) params.set('organizationId', filters.companies.join(','));
      const resolved = resolveDateFilter(filters.dateFilterKey, filters.dateCustomFrom, filters.dateCustomTo);
      if (resolved) { params.set('dateFrom', resolved.startDate); params.set('dateTo', resolved.endDate); }
      if (filters.amountMin) params.set('amountMin', filters.amountMin);
      if (filters.amountMax) params.set('amountMax', filters.amountMax);
      if (filters.showOverdue) params.set('overdue', 'true');
      if (sortOverride) { params.set('sortBy', sortOverride.sortBy); params.set('sortOrder', sortOverride.sortOrder); }

      const response = await apiClient.getRaw<ExpensePayableApiResponse>(`/expenses-payable?${params.toString()}`);
      if (response?.success) {
        const data = response.data || [];
        const paginationData = response.pagination || { page: 1, limit: 25, total: data.length, totalPages: 1 };
        setExpensePayableItems(data);
        setPagination(paginationData);
        setCurrentPage(paginationData.page);
        setAllFetchedVendors(prev => {
          const next = new Set(prev);
          data.forEach(item => { if (item.vendor_name && item.vendor_name !== 'Unknown Vendor') next.add(item.vendor_name); });
          return next;
        });
        setAllFetchedCategories(prev => {
          const next = new Set(prev);
          data.forEach(item => { if (item.category_name) next.add(item.category_name); });
          return next;
        });
        setAllFetchedOrganizations(prev => {
          const next = new Map(prev);
          data.forEach(item => { if (item.organization_id && item.organization_name) next.set(item.organization_id, item.organization_name); });
          return next;
        });
      } else {
        setExpensePayableItems([]);
        setPagination({ page: 1, limit: 25, total: 0, totalPages: 0 });
      }
    } catch {
      setExpensePayableItems([]);
      setPagination({ page: 1, limit: 25, total: 0, totalPages: 0 });
    } finally {
      setIsLoadingData(false);
    }
  }, [appliedFilters]);

  const fetchKPIs = useCallback(async (filters = appliedFilters) => {
    const resolved = resolveDateFilter(filters.dateFilterKey, filters.dateCustomFrom, filters.dateCustomTo);
    if (!resolved) return;
    setKpisLoading(true);
    try {
      const vendors = filters.vendors.length > 0 ? filters.vendors : undefined;
      const sourceTypes = filters.sourceTypes.length > 0 ? filters.sourceTypes : undefined;
      const range = { startDate: resolved.startDate, endDate: resolved.endDate };
      const [payables, agedPayables] = await Promise.all([
        fetchPayablesKPIs(range, vendors, sourceTypes),
        fetchAgedPayablesKPIs(range, vendors),
      ]);
      setPayablesKpis(payables);
      setAgedPayablesKpis(agedPayables);
    } catch {
      setPayablesKpis(null);
      setAgedPayablesKpis(null);
    } finally {
      setKpisLoading(false);
    }
  // appliedFilters intentionally excluded — called with explicit args
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFocusHighestItem = useCallback(() => {
    if (!payablesKpis?.highestItem) return;
    const { id, sourceType } = payablesKpis.highestItem;
    const key = `${sourceType}-${id}`;
    setHighlightedItemKey(key);
    const el = rowRefs.current.get(key);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setTimeout(() => setHighlightedItemKey(null), 2000);
    } else {
      // Item not on current page — fetch page 1 sorted by amount desc so the highest item lands first
      const focusFilters = { ...appliedFilters, statuses: [], categories: [], amountMin: '', amountMax: '', showOverdue: false };
      fetchData(1, focusFilters, { sortBy: 'amount', sortOrder: 'desc' });
    }
  }, [payablesKpis?.highestItem, appliedFilters, fetchData]);

  // Scroll to highlighted item once it appears in the rendered list; start 2s clear timer from here
  useEffect(() => {
    if (!highlightedItemKey) return;
    const el = rowRefs.current.get(highlightedItemKey);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const timer = setTimeout(() => setHighlightedItemKey(null), 2000);
    return () => clearTimeout(timer);
  }, [expensePayables, highlightedItemKey]);

  useEffect(() => {
    const applyVendorFilter = async () => {
      if (vendorFilter && !appliedFilters.vendors.includes(vendorFilter)) {
        const newFilters = { ...appliedFilters, vendors: [vendorFilter] };
        setSelectedVendors([vendorFilter]);
        setAppliedFilters(newFilters);
        setCurrentPage(1);
        await fetchData(1, newFilters);
        fetchKPIs(newFilters);
      } else if (!vendorFilter && appliedFilters.vendors.length > 0) {
        const newFilters = { ...appliedFilters, vendors: [] };
        setSelectedVendors([]);
        setAppliedFilters(newFilters);
        setCurrentPage(1);
        await fetchData(1, newFilters);
        fetchKPIs(newFilters);
      }
    };
    applyVendorFilter();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vendorFilter]);

  useEffect(() => {
    if (!vendorFilter) { fetchData(1); fetchKPIs(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hasFilterChanges = useMemo(() => (
    JSON.stringify(selectedVendors.slice().sort()) !== JSON.stringify(appliedFilters.vendors.slice().sort()) ||
    JSON.stringify(selectedSourceTypes.slice().sort()) !== JSON.stringify(appliedFilters.sourceTypes.slice().sort()) ||
    JSON.stringify(selectedStatuses.slice().sort()) !== JSON.stringify(appliedFilters.statuses.slice().sort()) ||
    JSON.stringify(selectedCategories.slice().sort()) !== JSON.stringify(appliedFilters.categories.slice().sort()) ||
    JSON.stringify(selectedCompanies.slice().sort()) !== JSON.stringify(appliedFilters.companies.slice().sort()) ||
    amountMin !== appliedFilters.amountMin ||
    amountMax !== appliedFilters.amountMax ||
    showOverdue !== appliedFilters.showOverdue
  ), [selectedVendors, selectedSourceTypes, selectedStatuses, selectedCategories, selectedCompanies, amountMin, amountMax, showOverdue, appliedFilters]);

  const hasActiveFilters = useMemo(() => (
    appliedFilters.vendors.length > 0 ||
    appliedFilters.sourceTypes.length > 0 ||
    appliedFilters.statuses.length > 0 ||
    appliedFilters.categories.length > 0 ||
    appliedFilters.companies.length > 0 ||
    appliedFilters.amountMin !== '' ||
    appliedFilters.amountMax !== '' ||
    appliedFilters.showOverdue
  ), [appliedFilters]);

  const uniqueVendors = useMemo(() => Array.from(allFetchedVendors).sort(), [allFetchedVendors]);
  const uniqueCategories = useMemo(() => Array.from(allFetchedCategories).sort(), [allFetchedCategories]);
  const uniqueOrganizations = useMemo(() =>
    Array.from(allFetchedOrganizations.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name)),
    [allFetchedOrganizations]
  );

  const handleApplyFilters = async (overrideFilters?: Partial<typeof appliedFilters>) => {
    if (amountMin !== '' && amountMax !== '' && parseFloat(amountMin) > parseFloat(amountMax)) {
      toast({ variant: 'destructive', description: 'Min amount cannot be greater than max amount.' });
      return;
    }
    const newFilters = {
      vendors: selectedVendors, sourceTypes: selectedSourceTypes,
      statuses: selectedStatuses, categories: selectedCategories, companies: selectedCompanies,
      dateFilterKey, dateCustomFrom, dateCustomTo, amountMin, amountMax, showOverdue,
      ...overrideFilters,
    };
    posthog.capture("table_filtered", {
      table: "expenses_payables",
      has_vendor_filter: selectedVendors.length > 0,
      has_source_type_filter: selectedSourceTypes.length > 0,
      has_status_filter: selectedStatuses.length > 0,
      has_category_filter: selectedCategories.length > 0,
      has_date_filter: dateFilterKey !== "all_time",
      has_amount_filter: amountMin !== "" || amountMax !== "",
      overdue_only: showOverdue,
    });
    setCurrentPage(1);
    await fetchData(1, newFilters);
    fetchKPIs(newFilters);
    setAppliedFilters(newFilters);
  };

  const clearFilters = () => {
    posthog.capture("table_filters_cleared", { table: "expenses_payables" });
    setSelectedVendors([]); setSelectedSourceTypes([]);
    setSelectedStatuses([]); setSelectedCategories([]); setSelectedCompanies([]);
    setAmountMin(''); setAmountMax('');
    setShowOverdue(false);
    const cleared = { vendors: [] as string[], sourceTypes: [] as string[], statuses: [], categories: [], companies: [] as string[], dateFilterKey, dateCustomFrom, dateCustomTo, amountMin: '', amountMax: '', showOverdue: false };
    setAppliedFilters(cleared);
    setCurrentPage(1);
    fetchData(1, cleared);
    fetchKPIs(cleared);
  };

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= pagination.totalPages) {
      posthog.capture("table_paginated", { table: "expenses_payables", page });
      setCurrentPage(page);
      fetchData(page);
    }
  };

  const fetchPaymentsForItem = async (item: ExpensePayableItem) => {
    if (loadingPayments.has(item.id) || vendorPaymentsCache.has(item.id) || item.source_type === 'payment') return;
    try {
      setLoadingPayments(prev => new Set(prev).add(item.id));
      const endpoint = item.source_type === 'bill'
        ? `/vendor-payments/by-bill?billId=${item.id}`
        : `/vendor-payments/by-expense?expenseId=${item.id}`;
      const itemPayments = await apiClient.get<VendorPayment[]>(endpoint);
      vendorPaymentsCache.set(item.id, itemPayments || []);
      setCacheVersion(v => v + 1);
    } catch {
      vendorPaymentsCache.set(item.id, []);
      setCacheVersion(v => v + 1);
    } finally {
      setLoadingPayments(prev => { const next = new Set(prev); next.delete(item.id); return next; });
    }
  };


  const periodLabel = (() => {
    if (!dateFilterKey) return null;
    if (dateFilterKey === 'custom') {
      if (!dateCustomFrom || !dateCustomTo) return 'Custom range';
      return `${format(dateCustomFrom, 'MM/dd/yyyy')} – ${format(dateCustomTo, 'MM/dd/yyyy')}`;
    }
    return DATE_FILTER_OPTIONS.find(o => o.key === dateFilterKey)?.label ?? null;
  })();

  const handleExportCsv = async () => {
    if (isExporting) return;
    setIsExporting(true);
    try {
      await exportExpensesCsv({
        vendors: appliedFilters.vendors,
        sourceTypes: appliedFilters.sourceTypes,
        statuses: appliedFilters.statuses,
        categories: appliedFilters.categories,
        showOverdue: appliedFilters.showOverdue,
        dateFilterKey: appliedFilters.dateFilterKey,
        dateCustomFrom: appliedFilters.dateCustomFrom,
        dateCustomTo: appliedFilters.dateCustomTo,
        amountMin: appliedFilters.amountMin,
        amountMax: appliedFilters.amountMax,
      });
      posthog.capture("csv_exported", { table: "expenses_payables" });
    } catch {
      toast({ variant: 'destructive', description: 'Failed to export CSV. Please try again.' });
    } finally {
      setIsExporting(false);
    }
  };

  // ── Sorting ─────────────────────────────────────────────────────────────
  const handleSort = (field: SortField) => {
    let newDir: 'asc' | 'desc' | null;
    if (sortField === field) {
      if (sortDirection === 'asc') { newDir = 'desc'; setSortDirection('desc'); }
      else if (sortDirection === 'desc') { newDir = null; setSortDirection(null); setSortField('due_date'); }
      else { newDir = 'asc'; setSortDirection('asc'); }
    } else {
      newDir = 'asc'; setSortField(field); setSortDirection('asc');
    }
    posthog.capture("table_sorted", { table: "expenses_payables", column: field, direction: newDir });
  };

  const aggregatedData = useMemo(() => ({ items: expensePayables.filter(i => i.source_type !== 'payment') }), [expensePayables]);

  const sortedData = useMemo(() => {
    const data = aggregatedData.items;
    if (!sortDirection) return data as ExpensePayableItem[];
    return [...data].sort((a, b) => {
      let aV: string | number | null | undefined, bV: string | number | null | undefined;
      switch (sortField) {
        case 'reference_number': aV = a.reference_number ?? ''; bV = b.reference_number ?? ''; break;
        case 'source_type': aV = a.source_type; bV = b.source_type; break;
        case 'vendor_name': aV = a.vendor_name; bV = b.vendor_name; break;
        case 'category_name': aV = a.category_name; bV = b.category_name; break;
        case 'txn_date': aV = new Date(a.txn_date).getTime(); bV = new Date(b.txn_date).getTime(); break;
        case 'due_date': aV = a.due_date ? new Date(a.due_date).getTime() : 0; bV = b.due_date ? new Date(b.due_date).getTime() : 0; break;
        case 'amount': aV = a.amount; bV = b.amount; break;
        case 'balance': aV = a.balance ?? 0; bV = b.balance ?? 0; break;
        case 'status': aV = a.status; bV = b.status; break;
        default: return 0;
      }
      if (aV < bV) return sortDirection === 'asc' ? -1 : 1;
      if (aV > bV) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [expensePayables, aggregatedData, sortField, sortDirection]);

  void cacheVersion;

  const SortableHeader = ({ field, children }: { field: SortField; children: React.ReactNode }) => {
    const isActive = sortField === field;
    return (
      <div onClick={() => handleSort(field)} className="flex items-center gap-1 cursor-pointer hover:text-foreground/80">
        {children}
        {isActive && sortDirection === 'asc' && <ArrowUp className="h-3 w-3" />}
        {isActive && sortDirection === 'desc' && <ArrowDown className="h-3 w-3" />}
        {!isActive && <ArrowUpDown className="h-3 w-3 opacity-30" />}
      </div>
    );
  };

  return (
    <Card ref={visibilityRef} className="m-6 shadow-md">
      <CardHeader>
        <div className="flex items-center justify-between gap-4">
          <div className="flex flex-col gap-1">
            <CardTitle className="text-lg md:text-xl">Expenses & Payables</CardTitle>
            {vendorFilter && <div className="text-sm text-muted-foreground">Filtered by vendor: <span className="font-medium">{vendorFilter}</span></div>}
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => setShowManageStatuses(true)}
              className="h-8 gap-1.5 text-xs text-muted-foreground hover:text-foreground" title="Manage bill statuses">
              <Settings2 className="h-3.5 w-3.5" /> Statuses
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button disabled={isExporting}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-muted hover:bg-muted/70 active:scale-95 transition-all text-sm font-medium text-muted-foreground hover:text-foreground disabled:opacity-40">
                  <Download className="h-4 w-4" />
                  <ChevronDown className="h-3.5 w-3.5 opacity-70" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-44">
                <DropdownMenuItem onClick={handleExportCsv} className="gap-2 cursor-pointer">
                  <Download className="h-4 w-4" /> {isExporting ? 'Exporting…' : 'Export to CSV'}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <DateFilter
              value={dateFilterKey}
              customFrom={dateCustomFrom}
              customTo={dateCustomTo}
              onChange={onDateFilterChange}
              label=""
              width="w-[160px]"
              showEmpty={false}
              pickerSide="left"
            />
          </div>
        </div>
      </CardHeader>
      <CardContent className="px-3 md:px-6">

        {/* Filter Bar */}
        <div className="mb-4 flex items-center gap-0 px-2.5 py-1.5 bg-muted/20 rounded-lg border">
          {/* Scrollable pills area */}
          <div className="flex items-center gap-2 overflow-x-auto flex-1 min-w-0 pr-2">
          <Filter className="h-3.5 w-3.5 text-muted-foreground/50 shrink-0" />

          {/* Company (view mode only) */}
          {activeViewId && (
            <>
              <Popover>
                <PopoverTrigger asChild>
                  <button className={`flex items-center gap-1 text-xs h-7 px-2 rounded border border-input hover:bg-muted/80 transition-colors shrink-0 w-[150px] ${selectedCompanies.length > 0 ? 'bg-muted/70' : 'bg-background'}`}>
                    <span className="text-muted-foreground shrink-0">Company:</span>
                    <span className="font-medium flex-1 truncate text-left">
                      {selectedCompanies.length === 0 ? 'All' : selectedCompanies.length === 1
                        ? uniqueOrganizations.find(o => o.id === selectedCompanies[0])?.name ?? '1 selected'
                        : `${selectedCompanies.length} selected`}
                    </span>
                    <ChevronDown className="h-3 w-3 text-muted-foreground shrink-0" />
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-[220px] p-1 max-h-60 overflow-y-auto" align="start">
                  {uniqueOrganizations.map(({ id, name }) => (
                    <button key={id}
                      onClick={() => setSelectedCompanies(prev => prev.includes(id) ? prev.filter(c => c !== id) : [...prev, id])}
                      className="w-full flex items-center gap-2 px-2 py-1.5 text-sm rounded hover:bg-muted/80 text-left">
                      <Check className={`h-3.5 w-3.5 shrink-0 ${selectedCompanies.includes(id) ? 'opacity-100' : 'opacity-0'}`} />
                      <span className="truncate">{name}</span>
                    </button>
                  ))}
                  {uniqueOrganizations.length === 0 && <p className="px-2 py-1.5 text-xs text-muted-foreground">No companies loaded</p>}
                </PopoverContent>
              </Popover>
              <div className="h-4 w-px bg-border shrink-0" />
            </>
          )}

          {/* Vendor */}
          <Popover>
            <PopoverTrigger asChild>
              <button className={`flex items-center gap-1 text-xs h-7 px-2 rounded border border-input hover:bg-muted/80 transition-colors shrink-0 w-[140px] ${selectedVendors.length > 0 ? 'bg-muted/70' : 'bg-background'}`}>
                <span className="text-muted-foreground shrink-0">Vendor:</span>
                <span className="font-medium flex-1 truncate text-left">
                  {selectedVendors.length === 0 ? 'All' : selectedVendors.length === 1 ? selectedVendors[0] : `${selectedVendors.length} selected`}
                </span>
                <ChevronDown className="h-3 w-3 text-muted-foreground shrink-0" />
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-[220px] p-1 max-h-60 overflow-y-auto" align="start">
              {uniqueVendors.map(name => (
                <button key={name}
                  onClick={() => setSelectedVendors(prev => prev.includes(name) ? prev.filter(v => v !== name) : [...prev, name])}
                  className="w-full flex items-center gap-2 px-2 py-1.5 text-sm rounded hover:bg-muted/80 text-left">
                  <Check className={`h-3.5 w-3.5 shrink-0 ${selectedVendors.includes(name) ? 'opacity-100' : 'opacity-0'}`} />
                  <span className="truncate">{name}</span>
                </button>
              ))}
              {uniqueVendors.length === 0 && <p className="px-2 py-1.5 text-xs text-muted-foreground">No vendors loaded</p>}
            </PopoverContent>
          </Popover>

          <div className="h-4 w-px bg-border shrink-0" />

          {/* Status */}
          <Popover>
            <PopoverTrigger asChild>
              <button className={`flex items-center gap-1 text-xs h-7 px-2 rounded border border-input hover:bg-muted/80 transition-colors shrink-0 w-[130px] ${selectedStatuses.length > 0 ? 'bg-muted/70' : 'bg-background'}`}>
                <span className="text-muted-foreground shrink-0">Status:</span>
                <span className="font-medium flex-1 truncate text-left">
                  {selectedStatuses.length === 0 ? 'All' : selectedStatuses.length === 1
                    ? formatStatusDisplay(selectedStatuses[0])
                    : `${selectedStatuses.length} selected`}
                </span>
                <ChevronDown className="h-3 w-3 text-muted-foreground shrink-0" />
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-[180px] p-1" align="start">
              {ALL_STATUSES.map(s => (
                <button key={s}
                  onClick={() => setSelectedStatuses(prev => prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s])}
                  className="w-full flex items-center gap-2 px-2 py-1.5 text-sm rounded hover:bg-muted/80 text-left">
                  <Check className={`h-3.5 w-3.5 shrink-0 ${selectedStatuses.includes(s) ? 'opacity-100' : 'opacity-0'}`} />
                  {formatStatusDisplay(s)}
                </button>
              ))}
            </PopoverContent>
          </Popover>

          <div className="h-4 w-px bg-border shrink-0" />

          {/* Category */}
          <Popover>
            <PopoverTrigger asChild>
              <button className={`flex items-center gap-1 text-xs h-7 px-2 rounded border border-input hover:bg-muted/80 transition-colors shrink-0 w-[150px] ${selectedCategories.length > 0 ? 'bg-muted/70' : 'bg-background'}`}>
                <span className="text-muted-foreground shrink-0">Category:</span>
                <span className="font-medium flex-1 truncate text-left">
                  {selectedCategories.length === 0 ? 'All' : selectedCategories.length === 1 ? selectedCategories[0] : `${selectedCategories.length} selected`}
                </span>
                <ChevronDown className="h-3 w-3 text-muted-foreground shrink-0" />
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-[220px] p-1 max-h-60 overflow-y-auto" align="start">
              {uniqueCategories.map(name => (
                <button key={name}
                  onClick={() => setSelectedCategories(prev => prev.includes(name) ? prev.filter(c => c !== name) : [...prev, name])}
                  className="w-full flex items-center gap-2 px-2 py-1.5 text-sm rounded hover:bg-muted/80 text-left">
                  <Check className={`h-3.5 w-3.5 shrink-0 ${selectedCategories.includes(name) ? 'opacity-100' : 'opacity-0'}`} />
                  <span className="truncate">{name}</span>
                </button>
              ))}
              {uniqueCategories.length === 0 && <p className="px-2 py-1.5 text-xs text-muted-foreground">No categories loaded</p>}
            </PopoverContent>
          </Popover>

          <div className="h-4 w-px bg-border shrink-0" />

          {/* Amount range */}
          <div className={`flex items-center gap-1 text-xs h-7 px-2 rounded border border-input shrink-0 transition-colors ${(amountMin || amountMax) ? 'bg-muted/70' : 'bg-background'}`}>
            <span className="text-muted-foreground">$</span>
            <input type="number" min="0" placeholder="Min" value={amountMin} onChange={e => setAmountMin(e.target.value)}
              className="w-14 bg-transparent outline-none text-xs" />
            <span className="text-muted-foreground/50">–</span>
            <input type="number" min="0" placeholder="Max" value={amountMax} onChange={e => setAmountMax(e.target.value)}
              className="w-14 bg-transparent outline-none text-xs" />
          </div>

          <div className="h-4 w-px bg-border shrink-0" />

          {/* Overdue */}
          <label className={`flex items-center gap-1.5 cursor-pointer select-none text-xs h-7 px-2 rounded border border-input hover:bg-muted/80 transition-colors shrink-0 whitespace-nowrap ${showOverdue ? 'bg-muted/70' : 'bg-background'}`}>
            <input type="checkbox" checked={showOverdue} onChange={e => setShowOverdue(e.target.checked)}
              className="h-3 w-3 rounded accent-primary" />
            <span>Overdue only</span>
          </label>

          </div>
          {/* Always-visible action buttons */}
          <div className="flex items-center gap-1.5 shrink-0 pl-2 border-l border-border">
            <Button variant="default" size="sm" onClick={() => handleApplyFilters()}
              className="h-7 text-xs px-2.5 gap-1" disabled={!hasFilterChanges || isLoadingData}>
              <Filter className="h-3 w-3" /> Apply
            </Button>
            <Button variant="outline" size="sm" onClick={clearFilters}
              className="h-7 text-xs px-2.5 gap-1" disabled={!hasActiveFilters || isLoadingData}>
              <X className="h-3 w-3" /> Reset
            </Button>
          </div>
        </div>

        {/* KPI cards */}
        <div className={`grid grid-cols-1 md:grid-cols-[3fr_1fr_1fr] gap-2 mb-4 transition-opacity ${kpisLoading ? 'opacity-60' : ''}`}>
          <KpiGroupCard items={[
            { label: "Total Bills & Expenses", value: formatAmount(payablesKpis?.total.amount ?? 0, 'USD'), compactValue: formatAmountCompact(payablesKpis?.total.amount ?? 0), sub: `${payablesKpis?.total.count ?? 0} items`, valueClass: 'text-foreground', period: periodLabel, tip: "Total number and amount of all bills and expenses in the selected period." },
            { label: "Paid", value: formatAmount(payablesKpis?.paid.amount ?? 0, 'USD'), compactValue: formatAmountCompact(payablesKpis?.paid.amount ?? 0), sub: `${payablesKpis?.paid.count ?? 0} items`, valueClass: 'text-emerald-600', period: periodLabel, tip: "Sum of vendor payments made for bills in the selected period, plus all expenses." },
            { label: "Open (Unpaid and Partial)", value: formatAmount(agedPayablesKpis?.total.amount ?? 0, 'USD'), compactValue: formatAmountCompact(agedPayablesKpis?.total.amount ?? 0), sub: `${agedPayablesKpis?.total.count ?? 0} items`, valueClass: 'text-orange-600', period: agedPayablesKpis ? `As of ${agedPayablesKpis.asOfDate}` : periodLabel, tip: "Total outstanding AP balance as of the period end date, from QuickBooks aged payables report." },
            { label: "Overdue", value: formatAmount(agedPayablesKpis?.overdue.amount ?? 0, 'USD'), compactValue: formatAmountCompact(agedPayablesKpis?.overdue.amount ?? 0), sub: `${agedPayablesKpis?.overdue.count ?? 0} items past due`, valueClass: 'text-red-600', period: agedPayablesKpis ? `As of ${agedPayablesKpis.asOfDate}` : periodLabel, tip: "Outstanding AP balance overdue by more than 30 days, from QuickBooks aged payables report.", labelBadges: [{ text: ">30 days", className: "bg-red-100 text-red-700" }] },
          ]} />
          <KpiGroupCard items={[
            { label: "Highest Bill / Expense", value: payablesKpis?.highestItem ? formatAmount(payablesKpis.highestItem.amount, payablesKpis.highestItem.currency) : '—', compactValue: payablesKpis?.highestItem ? formatAmountCompact(payablesKpis.highestItem.amount) : '—', sub: payablesKpis?.highestItem?.vendorName ?? 'No items', valueClass: 'text-violet-600', period: periodLabel, tip: "The single largest bill or expense in the selected period. Click to highlight it in the table.", onClick: payablesKpis?.highestItem ? handleFocusHighestItem : undefined },
          ]} />
          <KpiGroupCard items={[
            { label: "Spend Above Approved Amount", value: 'TBD', sub: '', valueClass: 'text-muted-foreground', period: periodLabel, tip: "Coming soon." },
            { label: "Deviated Invoices (%)", value: 'TBD', sub: '', valueClass: 'text-muted-foreground', period: periodLabel, tip: "Coming soon." },
          ]} />
        </div>

        {/* Table / Cards */}
        {isMobile ? (
          <div className="space-y-3">
            {sortedData.map((item: ExpensePayableItem) => {
              const itemKey = `${item.source_type}-${item.id}`;
              const isExpanded = expandedItemId === itemKey;
              const isHighlighted = highlightedItemKey === itemKey;
              return (
                <div key={itemKey} ref={el => { if (el) rowRefs.current.set(itemKey, el); else rowRefs.current.delete(itemKey); }}
                  className={isHighlighted ? 'rounded-lg ring-2 ring-yellow-400 bg-yellow-100 dark:bg-yellow-900/30 transition-colors' : ''}>
                  <MobileExpenseCard item={item} isExpanded={isExpanded}
                    onToggle={async () => {
                      if (!isExpanded && !vendorPaymentsCache.has(item.id)) await fetchPaymentsForItem(item);
                      setExpandedItemId(expandedItemId === itemKey ? null : itemKey);
                    }}
                    vendorPaymentsCache={vendorPaymentsCache}
                    isLoading={loadingPayments.has(item.id)}
                    onPaymentClick={p => { setSelectedPayment(p); setPaymentSheetOpen(true); }}
                  />
                </div>
              );
            })}
            {isLoadingData ? (
              <Card><CardContent className="p-8 text-center text-muted-foreground">
                <div className="flex flex-col items-center gap-3">
                  <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                  <span className="text-sm">Loading expenses…</span>
                </div>
              </CardContent></Card>
            ) : sortedData.length === 0 && (
              <Card><CardContent className="p-8 text-center text-muted-foreground">No expenses or payables found</CardContent></Card>
            )}
          </div>
        ) : (
          <div className="border rounded-lg overflow-x-auto" dir="ltr">
            <div style={{ minWidth: `${totalWidth + (colWidths.length - 1) * 12 + 24}px` }}>
              <div className="grid gap-3 p-3 bg-muted/30 border-b font-semibold text-sm [&>*]:min-w-0" style={{ gridTemplateColumns: gridTemplate }}>
                <div className="relative group overflow-hidden"><SortableHeader field="reference_number">Ref #</SortableHeader><ResizeHandle onMouseDown={e => startResize(0, e)} className="opacity-0 group-hover:opacity-100" /></div>
                {activeViewId && (
                  <div className="relative group overflow-hidden"><div>Company</div><ResizeHandle onMouseDown={e => startResize(1, e)} className="opacity-0 group-hover:opacity-100" /></div>
                )}
                <div className="relative group overflow-hidden"><SortableHeader field="source_type">Type</SortableHeader><ResizeHandle onMouseDown={e => startResize(companyColOffset + 1, e)} className="opacity-0 group-hover:opacity-100" /></div>
                <div className="relative group overflow-hidden"><SortableHeader field="vendor_name">Vendor</SortableHeader><ResizeHandle onMouseDown={e => startResize(companyColOffset + 2, e)} className="opacity-0 group-hover:opacity-100" /></div>
                <div className="relative group overflow-hidden"><SortableHeader field="category_name">Category</SortableHeader><ResizeHandle onMouseDown={e => startResize(companyColOffset + 3, e)} className="opacity-0 group-hover:opacity-100" /></div>
                <div className="relative group overflow-hidden"><SortableHeader field="status">Status</SortableHeader><ResizeHandle onMouseDown={e => startResize(companyColOffset + 4, e)} className="opacity-0 group-hover:opacity-100" /></div>
                <div className="relative group overflow-hidden"><div>Workflow</div><ResizeHandle onMouseDown={e => startResize(companyColOffset + 5, e)} className="opacity-0 group-hover:opacity-100" /></div>
                <div className="relative group overflow-hidden"><SortableHeader field="amount">Total Amt</SortableHeader><ResizeHandle onMouseDown={e => startResize(companyColOffset + 6, e)} className="opacity-0 group-hover:opacity-100" /></div>
                <div className="relative group overflow-hidden"><span>Paid</span><ResizeHandle onMouseDown={e => startResize(companyColOffset + 7, e)} className="opacity-0 group-hover:opacity-100" /></div>
                <div className="relative group overflow-hidden"><SortableHeader field="balance">Balance</SortableHeader><ResizeHandle onMouseDown={e => startResize(companyColOffset + 8, e)} className="opacity-0 group-hover:opacity-100" /></div>
                <div className="relative group overflow-hidden"><SortableHeader field="txn_date">Txn Date</SortableHeader><ResizeHandle onMouseDown={e => startResize(companyColOffset + 9, e)} className="opacity-0 group-hover:opacity-100" /></div>
                <div className="relative group overflow-hidden"><SortableHeader field="due_date">Due Date</SortableHeader><ResizeHandle onMouseDown={e => startResize(companyColOffset + 10, e)} className="opacity-0 group-hover:opacity-100" /></div>
                <div>Actions</div>
              </div>

              {sortedData.map((item: ExpensePayableItem) => {
                const itemKey = `${item.source_type}-${item.id}`;
                const relatedPayments = vendorPaymentsCache.get(item.id) || [];
                const isExpanded = expandedItemId === itemKey;

                const isHighlighted = highlightedItemKey === itemKey;
                return (
                  <div key={itemKey} className="group" ref={el => { if (el) rowRefs.current.set(itemKey, el); else rowRefs.current.delete(itemKey); }}>
                    <div
                      onClick={async () => {
                        if (!isExpanded && !vendorPaymentsCache.has(item.id)) await fetchPaymentsForItem(item);
                        setExpandedItemId(expandedItemId === itemKey ? null : itemKey);
                      }}
                      className={`grid gap-3 p-3 hover:bg-muted/10 transition-colors border-b [&>*]:min-w-0 cursor-pointer ${isExpanded ? 'bg-muted/5' : ''} ${isHighlighted ? 'bg-yellow-100 dark:bg-yellow-900/30' : ''}`}
                      style={{ gridTemplateColumns: gridTemplate }}
                    >
                      <div className="text-sm font-mono text-muted-foreground">{item.reference_number || '—'}</div>
                      {activeViewId && <TruncatedCell content={item.organization_name ?? '—'} className="text-sm text-muted-foreground" />}
                      <div><SourceTypeBadge sourceType={item.source_type} /></div>
                      <TruncatedCell content={item.vendor_name} className="text-sm" />
                      <TruncatedCell content={item.category_name} className="text-sm" />
                      <div><StatusBadge status={item.status} /></div>
                      <div onClick={e => e.stopPropagation()}>
                        <WorkflowStatusBadge item={item} statuses={workflowStatuses} onChange={handleWorkflowStatusChange} />
                      </div>
                      <div className="text-sm">{formatAmount(item.amount, item.currency)}</div>
                      <div className="text-sm">{item.balance != null ? formatAmount(item.amount - item.balance, item.currency) : item.status === 'paid' ? formatAmount(item.amount, item.currency) : '—'}</div>
                      <div className="text-sm">{item.balance != null ? formatAmount(item.balance, item.currency) : '—'}</div>
                      <div className="text-sm">{formatDate(item.txn_date)}</div>
                      <div className="text-sm">{formatDate(item.due_date)}</div>
                      <div onClick={e => e.stopPropagation()}>
                        <ActionsCell
                          item={item}
                          onAskChat={onAskChat}
                          onReminder={() => setReminderModal({
                            itemId: String(item.id),
                            label: `${item.source_type.charAt(0).toUpperCase() + item.source_type.slice(1)} — ${item.vendor_name}`,
                            type: item.source_type === 'bill' ? 'bill' : 'expense',
                          })}
                          onComment={() => setCommentsTarget({
                            referenceType: "bill",
                            referenceId: String(item.id),
                            label: `Bill — ${item.vendor_name}`,
                          })}
                          rowReminder={rowReminders.get(String(item.id))}
                          commentCount={commentCounts.get(String(item.id))}
                        />
                      </div>
                    </div>

                    {isExpanded && (
                      <div className="border-b bg-muted/5">
                        {loadingPayments.has(item.id) ? (
                          <div className="p-4 text-sm text-muted-foreground">Loading payments…</div>
                        ) : relatedPayments.length > 0 ? (
                          <PaymentDetails payments={relatedPayments}
                            onPaymentClick={p => { setSelectedPayment(p); setPaymentSheetOpen(true); }} />
                        ) : (
                          <div className="p-4 text-sm text-muted-foreground">No payments found for this {item.source_type}.</div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}

              {isLoadingData ? (
                <div className="p-8 text-center text-muted-foreground">
                  <div className="flex flex-col items-center gap-3">
                    <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                    <span className="text-sm">Loading expenses…</span>
                  </div>
                </div>
              ) : sortedData.length === 0 && (
                <div className="p-8 text-center text-muted-foreground">No expenses or payables found</div>
              )}
            </div>
          </div>
        )}

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="mt-4 flex items-center justify-between border-t pt-4">
            <div className="text-sm text-muted-foreground">
              Showing {((currentPage - 1) * pagination.limit) + 1} – {Math.min(currentPage * pagination.limit, pagination.total)} of {pagination.total} items
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage <= 1 || isLoadingData} className="gap-1">
                <ChevronLeft className="h-4 w-4" /> Previous
              </Button>
              <div className="flex items-center gap-1">
                {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                  let pageNum: number;
                  if (pagination.totalPages <= 5) pageNum = i + 1;
                  else if (currentPage <= 3) pageNum = i + 1;
                  else if (currentPage >= pagination.totalPages - 2) pageNum = pagination.totalPages - 4 + i;
                  else pageNum = currentPage - 2 + i;
                  return (
                    <Button key={pageNum} variant={pageNum === currentPage ? "default" : "outline"} size="sm"
                      onClick={() => handlePageChange(pageNum)} disabled={isLoadingData} className="w-8 h-8 p-0">
                      {pageNum}
                    </Button>
                  );
                })}
              </div>
              <Button variant="outline" size="sm" onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage >= pagination.totalPages || isLoadingData} className="gap-1">
                Next <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </CardContent>

      <VendorPaymentDetailSheet payment={selectedPayment} open={paymentSheetOpen} onClose={() => setPaymentSheetOpen(false)} />

      {reminderModal && (
        <ReminderCreateModal
          open={!!reminderModal}
          onClose={() => setReminderModal(null)}
          onCreated={(targetNames, text) => {
            if (reminderModal) setRowReminders(prev => new Map(prev).set(reminderModal.itemId, { text, targetNames }));
          }}
          entityType={reminderModal.type}
          entityId={reminderModal.itemId}
          entityLabel={reminderModal.label}
        />
      )}

      <CommentsSheet
        target={commentsTarget}
        currentUserId={currentUserId}
        role={role}
        onClose={() => setCommentsTarget(null)}
        onCommentCountChange={(referenceId, count) =>
          setCommentCounts((prev) => new Map(prev).set(referenceId, count))
        }
      />

      <ManageBillStatusesModal
        open={showManageStatuses}
        onClose={() => setShowManageStatuses(false)}
        onChanged={setWorkflowStatuses}
      />
    </Card>
  );
};

export default ExpensesPayables;
